import React, { useState, useEffect, useRef } from 'react';
import { obtenerActividadesExtracurricularesPorProfesor, eliminarActividadExtracurricular } from '../../services/ActividadesService';
import ModalActividadExtracurricular from './moda_actividad_Extracurricular';
import { FaChevronDown, FaChevronUp, FaFile, FaPencilAlt, FaFilter, FaSearch, FaCalendarAlt, FaTasks, FaTrash } from 'react-icons/fa';
import { SlidersHorizontal } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Componente para el encabezado del dashboard
const DashboardHeader = ({ onNuevaActividad, filtroActivo, cambiarFiltroActivo, actividadesCount }) => (
    <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-4 sm:mb-6">
        <div className="p-4 sm:p-6 border-b border-gray-100">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-xl sm:text-2xl font-semibold text-gray-800">
                        Actividades Extracurriculares
                    </h1>
                    <p className="text-gray-500 text-sm mt-1">
                        {actividadesCount} actividades en total
                    </p>
                </div>
                <div className="w-full sm:w-auto">
                    <button
                        onClick={onNuevaActividad}
                        className="w-full sm:w-auto px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors duration-200 font-medium flex items-center justify-center gap-2"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                        </svg>
                        Nueva Actividad
                    </button>
                </div>
            </div>
        </div>
    </div>
);

// Componente para la barra de filtros
const FiltrosBar = ({ 
    filtroActivo, 
    cambiarFiltroActivo, 
    filtroEstado, 
    setFiltroEstado,
    filtroFecha, 
    setFiltroFecha,
    filtroAlumno, 
    setFiltroAlumno,
    alumnosFiltrados,
    limpiarFiltros 
}) => (
    <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4 mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row items-stretch gap-3 sm:gap-4">
            <div className="flex items-center gap-2 flex-1 min-w-0">
                <FaTasks className="text-gray-400 flex-shrink-0" />
                    <select 
                        value={filtroEstado}
                        onChange={(e) => setFiltroEstado(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-10 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    >
                        <option value="todos">Todos los estados</option>
                        <option value="pendientes">Pendientes</option>
                        <option value="completadas">Completadas</option>
                    </select>
            </div>

            <div className="flex items-center gap-2 flex-1 min-w-0">
                <FaCalendarAlt className="text-gray-400 flex-shrink-0" />
                    <select 
                        value={filtroFecha}
                        onChange={(e) => setFiltroFecha(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-10 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    >
                        <option value="todas">Todas las fechas</option>
                        <option value="proximas">Próximas</option>
                        <option value="pasadas">Pasadas</option>
                    </select>
            </div>

            <div className="flex items-center gap-2 flex-1 min-w-0">
                <FaSearch className="text-gray-400 flex-shrink-0" />
                <div className="relative w-full">
                        <input
                            type="text"
                            value={filtroAlumno}
                            onChange={(e) => setFiltroAlumno(e.target.value)}
                        placeholder="Buscar por alumno..."
                        className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-10 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                        {filtroAlumno && alumnosFiltrados.length > 0 && (
                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                                {alumnosFiltrados.map((nombre) => (
                                    <div
                                        key={nombre}
                                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                                        onClick={() => setFiltroAlumno(nombre)}
                                    >
                                        {nombre}
                                    </div>
                                ))}
                            </div>
                        )}
                </div>
            </div>

            {(filtroEstado !== 'todos' || filtroFecha !== 'todas' || filtroAlumno !== '') && (
                <button
                    onClick={limpiarFiltros}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium transition-colors whitespace-nowrap"
                >
                    Limpiar filtros
                </button>
            )}
        </div>
                    </div>
                );

// Componente para la tarjeta de actividad
const ActividadCard = ({ actividad, esExpandida, onToggleExpand, onEditar, onEliminar }) => {
    const [height, setHeight] = useState(0);
    const contentRef = useRef(null);

    useEffect(() => {
        if (contentRef.current) {
            setHeight(esExpandida ? contentRef.current.scrollHeight : 0);
        }
    }, [esExpandida, actividad]);

    return (
        <div 
            className="bg-white rounded-xl p-4 sm:p-6 shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100"
        >
            <div className="flex items-start justify-between mb-4">
                <div className="flex-grow min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 flex-1">
                            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 tracking-tight line-clamp-2">
                                {actividad.titulo}
                            </h2>
                            <span className={`text-xs sm:text-sm px-3 py-1 rounded-full font-medium ${
                                actividad.completada 
                                ? 'bg-green-50 text-green-600'
                                : 'bg-amber-50 text-amber-600'
                            }`}>
                                {actividad.completada ? 'Completada' : 'Pendiente'}
                            </span>
                        </div>
                        <button
                            onClick={() => onToggleExpand(actividad.id_actividad)}
                            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            {esExpandida ? <FaChevronUp /> : <FaChevronDown />}
                        </button>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-500">
                        <span className="flex items-center">
                            <span className="w-2 h-2 rounded-full bg-blue-400 mr-2"></span>
                            {actividad.nombreNino}
                        </span>
                        <span className="flex items-center">
                            <span className="w-2 h-2 rounded-full bg-purple-400 mr-2"></span>
                            {new Date(actividad.fecha_limite).toLocaleDateString('es-ES', {
                                day: 'numeric',
                                month: 'long'
                            })}
                        </span>
                    </div>
                </div>
            </div>

            <div 
                ref={contentRef}
                style={{ 
                    maxHeight: `${height}px`,
                    opacity: height ? 1 : 0
                }}
                className="transition-all duration-300 ease-in-out overflow-hidden"
            >
                <div className="py-2">
                    <p className="text-gray-600 leading-relaxed mb-6">
                        {actividad.descripcion}
                    </p>

                    {actividad.archivo && (
                        <div className="mb-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <FaFile className="text-blue-500" />
                                    <span className="text-sm text-gray-600">Material adjunto</span>
                                </div>
                                <a 
                                    href={actividad.archivo}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-blue-500 hover:text-blue-700 font-medium transition-colors"
                                >
                                    Ver archivo
                                </a>
                            </div>
                        </div>
                    )}

                    {actividad.completada && (
                        <div className="space-y-4 mt-6 p-4 bg-green-50 rounded-xl border border-green-100">
                            {actividad.observaciones && (
                                <div>
                                    <h4 className="text-sm font-medium text-green-800 mb-2">Observaciones del padre:</h4>
                                    <p className="text-sm text-green-700">{actividad.observaciones}</p>
                                </div>
                            )}
                            
                            {actividad.archivo_padre && (
                                <div className="flex items-center justify-between pt-3 border-t border-green-200">
                                    <div className="flex items-center gap-3">
                                        <FaFile className="text-green-600" />
                                        <span className="text-sm text-green-700">Archivo de respuesta</span>
                                    </div>
                                    <a 
                                        href={actividad.archivo_padre}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm text-green-600 hover:text-green-800 font-medium transition-colors"
                                    >
                                        Ver archivo
                                    </a>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
                    
            {!actividad.completada && (
                <div className="mt-6 pt-4 border-t border-gray-100 flex flex-col sm:flex-row gap-2 sm:gap-0 sm:justify-between sm:items-center">
                    <button
                        onClick={() => onEditar(actividad)}
                        className="flex items-center justify-center gap-2 px-4 py-1.5 text-sm text-blue-500 hover:text-blue-700 font-medium transition-colors"
                    >
                        <FaPencilAlt /> 
                        Editar
                    </button>
                    <button
                        onClick={() => onEliminar(actividad)}
                        className="flex items-center justify-center gap-2 px-4 py-1.5 text-sm text-red-500 hover:text-red-700 font-medium transition-colors"
                    >
                        <FaTrash />
                        Eliminar
                    </button>
                </div>
            )}
        </div>
    );
};

// Componente principal del dashboard
const ActividadExtracurricularDashboard = () => {
    const [actividades, setActividades] = useState([]);
    const [actividadesFiltradas, setActividadesFiltradas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modalAbierto, setModalAbierto] = useState(false);
    const [actividadSeleccionada, setActividadSeleccionada] = useState(null);
    const [actividadExpandida, setActividadExpandida] = useState(null);
    const [user, setUser] = useState(null);
    const [filtroEstado, setFiltroEstado] = useState('todos');
    const [filtroFecha, setFiltroFecha] = useState('todas');
    const [filtroAlumno, setFiltroAlumno] = useState('');
    const [alumnosFiltrados, setAlumnosFiltrados] = useState([]);
    const [paginaActual, setPaginaActual] = useState(1);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
    const ITEMS_POR_PAGINA = 6;

    // Efecto para manejar el cambio de tamaño de la ventana
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 640);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const userString = localStorage.getItem('user');
        if (userString) {
            setUser(JSON.parse(userString));
        }
    }, []);

    const cargarActividades = async () => {
        try {
            if (!user) return;
            
            const response = await obtenerActividadesExtracurricularesPorProfesor(user.id);
            // Si no hay actividades, establece un array vacío sin mostrar error
            if (!response.actividades || response.actividades.length === 0) {
                setActividades([]);
            } else {
                setActividades(response.actividades);
            }
            setLoading(false);
            setError(null);
        } catch (error) {
            console.error('Error al cargar actividades:', error);
            // Solo mostrar error si no es por falta de actividades
            if (!error.message.includes('No se encontraron actividades')) {
                toast.error('Error al cargar las actividades: ' + (error.message || 'Error de conexión'), {
                    position: 'top-right',
                    autoClose: 3000
                });
            }
            setActividades([]);
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user) {
            cargarActividades();
        }
    }, [user]);

    // Resetear el estado expandido cuando cambia la página o los filtros
    useEffect(() => {
        setActividadExpandida(null);
    }, [paginaActual, filtroEstado, filtroFecha, filtroAlumno]);

    // Modificar la función toggleExpandirActividad para manejar mejor el estado
    const toggleExpandirActividad = (actividadId) => {
        // Primero cerrar la actividad expandida actual si existe
        if (actividadExpandida) {
            setActividadExpandida(null);
            // Pequeño timeout antes de expandir la nueva actividad
            if (actividadExpandida !== actividadId) {
                setTimeout(() => {
                    setActividadExpandida(actividadId);
                }, 100);
            }
        } else {
            setActividadExpandida(actividadId);
        }
    };

    // Modificar el useEffect de filtrado para manejar mejor la paginación
    useEffect(() => {
        const fechaActual = new Date();
        let actividadesFiltradas = [...actividades];

        if (filtroEstado === 'pendientes') {
            actividadesFiltradas = actividadesFiltradas.filter(act => !act.completada);
        } else if (filtroEstado === 'completadas') {
            actividadesFiltradas = actividadesFiltradas.filter(act => act.completada);
        }

        if (filtroFecha === 'proximas') {
            actividadesFiltradas = actividadesFiltradas.filter(act => 
                new Date(act.fecha_limite) >= fechaActual
            );
        } else if (filtroFecha === 'pasadas') {
            actividadesFiltradas = actividadesFiltradas.filter(act => 
                new Date(act.fecha_limite) < fechaActual
            );
        }

        if (filtroAlumno) {
            actividadesFiltradas = actividadesFiltradas.filter(act => 
                act.nombreNino.toLowerCase().includes(filtroAlumno.toLowerCase())
            );
        }

        setActividadesFiltradas(actividadesFiltradas);
        
        // Calcular la nueva página actual basada en los resultados filtrados
        const nuevoPaginaActual = Math.min(
            paginaActual,
            Math.max(1, Math.ceil(actividadesFiltradas.length / ITEMS_POR_PAGINA))
        );
        if (nuevoPaginaActual !== paginaActual) {
            setPaginaActual(nuevoPaginaActual);
        }
    }, [actividades, filtroEstado, filtroFecha, filtroAlumno]);

    useEffect(() => {
        const nombresUnicos = [...new Set(actividades.map(act => act.nombreNino))];
        const alumnosFiltrados = nombresUnicos.filter(nombre =>
            nombre.toLowerCase().includes(filtroAlumno.toLowerCase())
        );
        setAlumnosFiltrados(alumnosFiltrados);
    }, [filtroAlumno, actividades]);

    const limpiarFiltros = () => {
        setFiltroEstado('todos');
        setFiltroFecha('todas');
        setFiltroAlumno('');
    };

    const abrirModal = (actividad = null) => {
        if (actividad && actividad.completada) return;
        setActividadSeleccionada(actividad);
        setModalAbierto(true);
    };

    const cerrarModal = () => {
        setModalAbierto(false);
        setActividadSeleccionada(null);
    };

    const onActividadGuardada = () => {
        cargarActividades();
        cerrarModal();
        toast.success('Actividad guardada exitosamente', {
            position: 'top-right',
            autoClose: 3000
        });
    };

    const handleEliminarActividad = async (actividad) => {
        if (!actividad || actividad.completada) {
            toast.error('No se puede eliminar una actividad completada', {
                position: 'top-right',
                autoClose: 4000
            });
            return;
        }

        try {
            // Mostrar toast de proceso
            toast.info('Eliminando actividad...', {
                position: "top-right",
                autoClose: 2000,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                className: 'z-50',
                style: {
                    fontFamily: '"Exo 2", sans-serif',
                    fontWeight: 500
                }
            });

                await eliminarActividadExtracurricular(actividad.id_actividad);
            
            // Mostrar toast de éxito
            toast.success('¡Actividad eliminada exitosamente!', {
                position: "top-right",
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                className: 'z-50',
                style: {
                    fontFamily: '"Exo 2", sans-serif',
                    fontWeight: 500
                }
            });

            // Esperar un momento para que se muestre el toast
            await new Promise(resolve => setTimeout(resolve, 500));
            
            // Recargar la lista de actividades
            cargarActividades();
            } catch (error) {
            toast.error('Error al eliminar la actividad: ' + error.message, {
                position: "top-right",
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                className: 'z-50',
                style: {
                    fontFamily: '"Exo 2", sans-serif',
                    fontWeight: 500
                }
            });
        }
    };

    const confirmarEliminacion = (actividad) => {
        const toastId = 'delete-confirmation';
        const closeToast = () => toast.dismiss(toastId);
        
        const DeleteConfirmation = () => (
            <div>
                <p className="font-bold mb-2">¿Está seguro de eliminar esta actividad?</p>
                <p className="mb-4">Esta acción no se puede deshacer.</p>
                <div className="flex justify-end space-x-2">
                    <button
                        onClick={() => closeToast()}
                        className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={() => {
                            closeToast();
                            handleEliminarActividad(actividad);
                        }}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                        Eliminar
                    </button>
                </div>
            </div>
        );

        toast.warn(<DeleteConfirmation />, {
            toastId,
            position: "top-center",
            autoClose: false,
            hideProgressBar: true,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: false,
            closeButton: false,
            className: "rounded-lg",
        });
    };

    // Calcular el total de páginas
    const totalPaginas = Math.ceil(actividadesFiltradas.length / ITEMS_POR_PAGINA);

    // Obtener las actividades de la página actual
    const actividadesPaginadas = actividadesFiltradas.slice(
        (paginaActual - 1) * ITEMS_POR_PAGINA,
        paginaActual * ITEMS_POR_PAGINA
    );

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-4 sm:p-8">
            <ToastContainer position="top-right" autoClose={3000} />
            <div className="max-w-7xl mx-auto">
                <DashboardHeader 
                    onNuevaActividad={() => abrirModal()}
                    actividadesCount={actividades.length}
                />

                <FiltrosBar 
                    filtroEstado={filtroEstado}
                    setFiltroEstado={setFiltroEstado}
                    filtroFecha={filtroFecha}
                    setFiltroFecha={setFiltroFecha}
                    filtroAlumno={filtroAlumno}
                    setFiltroAlumno={setFiltroAlumno}
                    alumnosFiltrados={alumnosFiltrados}
                    limpiarFiltros={limpiarFiltros}
                />

                {actividadesFiltradas.length > 0 ? (
                    <>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                            {isMobile 
                                ? actividadesFiltradas.map((actividad) => (
                                    <ActividadCard
                                        key={actividad.id_actividad}
                                        actividad={actividad}
                                        esExpandida={actividadExpandida === actividad.id_actividad}
                                        onToggleExpand={toggleExpandirActividad}
                                        onEditar={abrirModal}
                                        onEliminar={confirmarEliminacion}
                                    />
                                ))
                                : actividadesPaginadas.map((actividad) => (
                                    <ActividadCard
                                        key={actividad.id_actividad}
                                        actividad={actividad}
                                        esExpandida={actividadExpandida === actividad.id_actividad}
                                        onToggleExpand={toggleExpandirActividad}
                                        onEditar={abrirModal}
                                        onEliminar={confirmarEliminacion}
                                    />
                                ))
                            }
                        </div>

                        {/* Paginación solo visible en desktop */}
                        {totalPaginas > 1 && !isMobile && (
                            <div className="flex justify-center mt-6 sm:mt-8">
                                <nav className="flex flex-wrap items-center gap-2">
                                    <button
                                        onClick={() => setPaginaActual(p => Math.max(1, p - 1))}
                                        disabled={paginaActual === 1}
                                        className={`px-3 py-1 rounded-lg text-sm ${
                                            paginaActual === 1
                                            ? 'bg-gray-100 text-gray-400'
                                            : 'bg-white text-gray-700 hover:bg-gray-50'
                                        }`}
                                    >
                                        Anterior
                                    </button>
                                    
                                    {[...Array(totalPaginas)].map((_, i) => (
                                        <button
                                            key={i + 1}
                                            onClick={() => setPaginaActual(i + 1)}
                                            className={`px-3 py-1 rounded-lg text-sm ${
                                                paginaActual === i + 1
                                                ? 'bg-indigo-600 text-white'
                                                : 'bg-white text-gray-700 hover:bg-gray-50'
                                            }`}
                                        >
                                            {i + 1}
                                        </button>
                                    ))}

                                    <button
                                        onClick={() => setPaginaActual(p => Math.min(totalPaginas, p + 1))}
                                        disabled={paginaActual === totalPaginas}
                                        className={`px-3 py-1 rounded-lg text-sm ${
                                            paginaActual === totalPaginas
                                            ? 'bg-gray-100 text-gray-400'
                                            : 'bg-white text-gray-700 hover:bg-gray-50'
                                        }`}
                                    >
                                        Siguiente
                                    </button>
                                </nav>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="min-h-[60vh] flex items-center justify-center">
                        <div className="text-center p-4 sm:p-8 max-w-md">
                            <div className="mb-6">
                                <svg className="mx-auto h-12 sm:h-16 w-12 sm:w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2M7 7h10" />
                                </svg>
                            </div>
                            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">No hay actividades registradas</h2>
                            <p className="text-gray-600 mb-6">Aún no se han registrado actividades extracurriculares. Comienza creando una nueva actividad.</p>
                            <button
                                onClick={() => abrirModal()}
                                className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-200 font-medium gap-2"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                                </svg>
                                Crear primera actividad
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {modalAbierto && (
                <ModalActividadExtracurricular
                    actividad={actividadSeleccionada}
                    onClose={cerrarModal}
                    onSuccess={onActividadGuardada}
                />
            )}
        </div>
    );
};

export default ActividadExtracurricularDashboard;
